import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ArrowUpRight, Check, X, CircleSlash, Loader2, Circle } from "lucide-react";
import { cn } from "../../lib/cn";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";
import { BLOCK_SURFACE } from "./blockSurface";

/**
 * Steps - a vertical stepper / progress tracker showing an ordered process and
 * each step's status. Renderer for the K2 `steps` block
 * (`docs/contracts/block-registry.md`): the platform's chat `BlockRenderer`
 * passes a `steps` block's `props` straight through, so the prop names match
 * the block payload exactly.
 *
 * Block props (`{ title?, steps, elapsedMs?, receipt? }`):
 *   - `title?` - optional heading.
 *   - `steps` - the ordered steps, each
 *     `{ id, label, description?, status }`, where `status` is one of
 *     `"pending" | "in_progress" | "completed" | "failed" | "cancelled"`.
 *   - `elapsedMs?` - total elapsed time in ms, shown next to the title.
 *   - `receipt?` - when the whole process has settled, the durable outcome
 *     (`{ outcome, summary, identifiers?, at }`); rendered above the steps.
 *
 * Client-only props (never part of the streamed block): `className`.
 *
 * Merges tool-ui's Plan + Progress Tracker (their split is not worth two block
 * types). Each step shows a status glyph on a connecting rail; the in-progress
 * step spins (disabled under reduced motion). Token-themed (`text-success`,
 * `text-destructive`, `border-border`, ...) with no hardcoded colors. SSR-safe.
 */

/** Source: tool-ui Plan + Progress Tracker (merged).
 * `waiting` is CLIENT-ONLY (never part of the streamed block contract): the
 * process is parked on the USER (an approval gate). It spins like in_progress
 * — motion draws the eye to the one step that needs the user (owner call
 * 2026-07-24, overriding the static-glyph draft) — but in the WARNING tone,
 * so "working" (primary) and "waiting on you" (warm) stay distinguishable at
 * a glance; the stative caption and the in-step action carry the rest.
 *
 * `offered` is CLIENT-ONLY too: the terminal INVITATION state. Every work
 * step is done and only an optional user action remains (e.g. sharing a
 * finished creation). It must read as "ready for you", never as stuck — so
 * no grey pending circle, no spinner (nothing is running): a soft primary
 * fill with the step's forward glyph, letting the caption + in-step action
 * carry the invitation (owner ruling 2026-07-24: "share never gets stuck"). */
export type StepStatus =
  | "pending"
  | "in_progress"
  | "waiting"
  | "offered"
  | "completed"
  | "failed"
  | "cancelled";

export interface StepItem {
  /** Stable id for the step. */
  id: string;
  /** The step's label. */
  label: string;
  /** Optional supporting detail. */
  description?: string;
  /** Current status. Drives the glyph + color. */
  status: StepStatus;
  /** CLIENT-ONLY (never streamed): an action rendered in the step's content
   * column, under the description — the Material vertical-stepper pattern
   * (actions live with the step they gate, not in a card footer). */
  action?: React.ReactNode;
}

const stepsVariants = cva(
  cn("not-prose flex flex-col gap-3", BLOCK_SURFACE),
);

const GLYPH: Record<StepStatus, React.ComponentType<{ className?: string }>> = {
  pending: Circle,
  in_progress: Loader2,
  waiting: Loader2,
  offered: ArrowUpRight,
  completed: Check,
  failed: X,
  cancelled: CircleSlash,
};

const GLYPH_TONE: Record<StepStatus, string> = {
  pending: "border-border bg-card text-muted-foreground",
  in_progress: "border-primary/40 bg-primary/10 text-primary",
  waiting: "border-warning/40 bg-warning/10 text-warning",
  offered: "border-primary bg-primary text-primary-foreground",
  completed: "border-success/40 bg-success/15 text-success",
  failed: "border-destructive/40 bg-destructive/15 text-destructive",
  cancelled: "border-border bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<StepStatus, string> = {
  pending: "Pending",
  in_progress: "In progress",
  waiting: "Waiting on you",
  offered: "Ready for you",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

/** Format elapsed milliseconds into a compact human string. */
function formatElapsed(ms: number | undefined): string | undefined {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return undefined;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${rem}s`;
}

export interface StepsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">, VariantProps<typeof stepsVariants> {
  /** Optional heading. */
  title?: string;
  /** The ordered steps. Required. */
  steps: StepItem[];
  /** Total elapsed time in ms, shown next to the title. */
  elapsedMs?: number;
  /** Settled outcome for the whole process, rendered above the steps. */
  receipt?: BlockReceipt | unknown;
}

export const Steps = React.forwardRef<HTMLDivElement, StepsProps>(function Steps(
  { className, title, steps, elapsedMs, receipt, ...rest },
  ref,
) {
  const elapsed = formatElapsed(elapsedMs);
  const settled = readReceipt(receipt);

  return (
    <div ref={ref} className={cn(stepsVariants(), className)} data-slot="steps" {...rest}>
      {title || elapsed ? (
        <div className="flex items-baseline justify-between gap-3">
          {title ? (
            <p className="m-0 text-sm font-semibold leading-none text-foreground">{title}</p>
          ) : (
            <span />
          )}
          {elapsed ? (
            <span className="text-xs tabular-nums text-muted-foreground">{elapsed}</span>
          ) : null}
        </div>
      ) : null}

      {settled ? <BlockReceiptView receipt={settled} /> : null}

      <ol className="flex flex-col">
        {steps.map((step, i) => {
          const Glyph = GLYPH[step.status];
          const isLast = i === steps.length - 1;
          // waiting spins too (owner 2026-07-24): motion pulls the eye to the
          // step that needs the user; the warning tone is what separates it
          // from the primary working spinner.
          const spin = step.status === "in_progress" || step.status === "waiting";
          return (
            <li key={step.id} className="flex gap-3">
              {/* Rail column: glyph + connecting line */}
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    "inline-flex size-6 shrink-0 items-center justify-center rounded-full border",
                    GLYPH_TONE[step.status],
                    // The invitation node SETTLES once (one-shot pop, host-defined
                    // keyframes) and then rests -- motion loops would read as
                    // "still processing", the exact feeling this state removes.
                    step.status === "offered" &&
                      "animate-[stPop_0.4s_cubic-bezier(0.16,1,0.3,1)] motion-reduce:animate-none",
                  )}
                >
                  <Glyph
                    className={cn("size-3.5", spin && "animate-spin motion-reduce:animate-none")}
                    aria-hidden
                  />
                </span>
                {!isLast ? (
                  <span
                    className={cn(
                      "w-px flex-1",
                      step.status === "completed" ? "bg-success/40" : "bg-border",
                    )}
                    aria-hidden
                  />
                ) : null}
              </div>
              {/* Content column. The label's first-line box is leading-6 (24px) —
                  the exact glyph height — so with both columns top-aligned the
                  glyph's center and the FIRST LINE's center coincide (the
                  Carbon-derived rule: the indicator tracks the first line, never
                  the label+caption block, which would drift as captions wrap).
                  m-0 on every <p> self-defends against preflight-less hosts,
                  where the UA's 1em paragraph margin pushed the label ~14px off
                  the glyph. */}
              <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-4")}>
                <p
                  className={cn(
                    "m-0 text-sm font-medium leading-6",
                    step.status === "pending" ? "text-muted-foreground" : "text-foreground",
                  )}
                >
                  {step.label}
                  <span className="sr-only"> ({STATUS_LABEL[step.status]})</span>
                </p>
                {step.description ? (
                  // A waiting step's caption is a status message (WCAG 4.1.3):
                  // announce the park politely without stealing focus.
                  <p
                    className="m-0 mt-0.5 text-xs leading-relaxed text-muted-foreground"
                    role={step.status === "waiting" || step.status === "offered" ? "status" : undefined}
                  >
                    {step.description}
                  </p>
                ) : null}
                {step.action ? <div className="mt-2">{step.action}</div> : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
});

Steps.displayName = "Steps";

export { stepsVariants };
export type StepsVariantProps = VariantProps<typeof stepsVariants>;
